/* eslint-disable react/no-unknown-property */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as AlertIcon } from 'assets/icons_3/alert-circle.svg';
import { ReactComponent as PhoneIcon } from 'assets/icons_3/phone.svg';
import { ReactComponent as MailIcon } from 'assets/icons_3/email.svg';
import { ReactComponent as WebIcon } from 'assets/icons_3/web.svg';
import Badge from 'components/UI_kit_3/Badge/Badge';
import Button from 'components/UI_kit_3/Button/Button';
import Tooltip from 'components/UI_kit_3/Popover/Tooltip/Tooltip';
import { useAppSelector, useNotification } from 'hooks';
import useAsyncFetch from 'hooks/async-fetch';
import trialService from 'services/api/customer/trial-service';
import { numbersDeclension } from 'utils/formatters';

import styles from './TrialBanner.module.scss';

/** Начиная с этого количества дней предупреждение становится оранжевым */
const WARNING_DAYS = 3;

interface ITrialBannerProps {
  className?: string;
}

const TrialBanner: React.FC<ITrialBannerProps> = ({ className }) => {
  const [t] = useTranslation();
  const notification = useNotification();
  const user = useAppSelector((state) => state.user);

  // блок триала показывается только корневому пользователю-саморегистранту
  const isRootUser = !!user.id && user.parentUserId === user.id;

  const [, , status] = useAsyncFetch({
    async asyncFunc() {
      const { data } = await trialService.getStatus();
      return data.data;
    },
    initialState: null,
    immediate: isRootUser,
  });

  const [requestExtension, requestLoading] = useAsyncFetch({
    async asyncFunc() {
      await trialService.requestExtension();
      notification.success(undefined, t('trial.extension_requested'));
    },
    errorNote: true,
  });

  if (!isRootUser || !status?.selfRegistered) return null;

  const daysLeft = Math.max(0, status.daysLeft ?? 0);
  const isWarning = status.warning || daysLeft <= WARNING_DAYS;
  const daysLabel = `${daysLeft} ${numbersDeclension(daysLeft, [
    t('trial.day_1'),
    t('trial.day_2'),
    t('trial.day_5'),
  ])}`;

  return (
    <Tooltip
      className={styles.tooltip}
      position="bottom-right"
      text={(
        <>
          <div className={styles.hint}>{t('trial.hint')}</div>
          <div className={styles.contactsTitle}>{t('contacts.self')}</div>
          <ul className={styles.contacts}>
            <li>
              <PhoneIcon />
              <span>+998 71 203 30 22</span>
            </li>
            <li>
              <MailIcon />
              <span>support@pro-data.tech</span>
            </li>
            <li>
              <WebIcon />
              <span>pro-data.tech</span>
            </li>
          </ul>
        </>
      )}
    >
      <div className={className}>
        <Badge
          className={styles.banner}
          variant={isWarning ? 'orange' : 'gray'}
          size="lg"
          qaElement="trial-banner"
        >
          <AlertIcon />
          <span qa-element="trial-days-left">{t('trial.period_left', { days: daysLabel })}</span>
          <span className={styles.divider}>·</span>
          <Button
            variant="link"
            disabled={requestLoading}
            onClick={() => requestExtension()}
            qa-element="trial-extension-request"
          >
            {t('trial.request_extension')}
          </Button>
        </Badge>
      </div>
    </Tooltip>
  );
};

export default TrialBanner;
